/**
 * COP Dashboard - Status Tab Component
 * Displays the most current status entries for each device and status type
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  TablePagination,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  RadioButtonChecked as RadioButtonCheckedIcon,
  SettingsInputComponent as StatusIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { copApiService, DeviceStatusEntry } from '../services/copApi';

interface StatusTabProps {
  selectedCourse?: string;
  selectedDeviceType?: string;
  selectedEntityType?: string;
  selectedEntity?: string;
  devicesInBounds?: string[];
  onStatusEntryClick?: (entries: DeviceStatusEntry[]) => void;
}

const StatusTab: React.FC<StatusTabProps> = ({
  selectedCourse,
  selectedDeviceType,
  selectedEntityType,
  selectedEntity,
  devicesInBounds,
  onStatusEntryClick
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = theme.palette.primary.main;
  
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '76, 175, 80';
  };
  const primaryRgb = hexToRgb(primaryColor);

  const colors = {
    cardBg: isDark
      ? 'linear-gradient(135deg, rgba(30, 32, 40, 0.98), rgba(24, 26, 32, 0.99))'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(245, 247, 250, 0.95))',
    border: isDark ? `rgba(${primaryRgb}, 0.25)` : `rgba(${primaryRgb}, 0.3)`,
    headerBg: isDark ? `rgba(${primaryRgb}, 0.05)` : `rgba(${primaryRgb}, 0.05)`,
    text: isDark ? 'white' : '#1a1a2e',
    textSecondary: isDark ? '#9e9e9e' : '#666',
    textTertiary: isDark ? '#757575' : '#999',
    subtleBg: isDark ? `rgba(${primaryRgb}, 0.05)` : `rgba(${primaryRgb}, 0.05)`,
  };

  const [statusEntries, setStatusEntries] = useState<DeviceStatusEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<DeviceStatusEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const loadStatusEntries = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await copApiService.getDeviceStatus(selectedCourse, selectedDeviceType, selectedEntityType);
      setStatusEntries(response.status_entries);
    } catch (err) {
      setError('Failed to load device status');
    } finally {
      setLoading(false);
    }
  }, [selectedCourse, selectedDeviceType, selectedEntityType]);

  useEffect(() => {
    loadStatusEntries();
  }, [loadStatusEntries]);

  useEffect(() => {
    setSelectedDeviceId(null);
    if (onStatusEntryClick) onStatusEntryClick([]);
  }, [selectedCourse, selectedDeviceType, selectedEntityType, selectedEntity, onStatusEntryClick]);

  useEffect(() => {
    let filtered = statusEntries;
    if (selectedEntity && selectedEntity !== 'All') {
      filtered = filtered.filter(entry => entry.entity_id === selectedEntity);
    }
    // Empty array is truthy in JS — only apply map filter when we have ids.
    if (devicesInBounds && devicesInBounds.length > 0) {
      filtered = filtered.filter(entry => devicesInBounds.includes(entry.device_id));
    }
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(entry => 
        entry.device_id.toLowerCase().includes(term) ||
        (entry.entity_id && entry.entity_id.toLowerCase().includes(term)) ||
        (entry.entity_type && entry.entity_type.toLowerCase().includes(term)) ||
        entry.status_type.toLowerCase().includes(term) ||
        entry.status_state.toLowerCase().includes(term)
      );
    }
    setFilteredEntries(filtered);
  }, [searchTerm, statusEntries, devicesInBounds, selectedEntity]);

  const getStatusTypeColor = (statusType: string): { bg: string; text: string; border: string } => {
    const type = statusType?.toLowerCase() || '';
    if (type.includes('power')) return { bg: 'rgba(76,175,80,0.12)', text: '#66bb6a', border: 'rgba(76,175,80,0.25)' };
    if (type.includes('cellular')) return { bg: 'rgba(33,150,243,0.12)', text: '#64b5f6', border: 'rgba(33,150,243,0.25)' };
    if (type.includes('wifi')) return { bg: 'rgba(156,39,176,0.12)', text: '#ba68c8', border: 'rgba(156,39,176,0.25)' };
    if (type.includes('bluetooth')) return { bg: 'rgba(0,188,212,0.12)', text: '#4dd0e1', border: 'rgba(0,188,212,0.25)' };
    if (type.includes('airplane')) return { bg: 'rgba(255,152,0,0.12)', text: '#ffb74d', border: 'rgba(255,152,0,0.25)' };
    return { bg: 'rgba(117,117,117,0.12)', text: '#9e9e9e', border: 'rgba(117,117,117,0.25)' };
  };

  const getStatusIcon = (state: string) => {
    const stateUpper = state?.toUpperCase() || '';
    if (stateUpper === 'ON' || stateUpper === 'ENABLED' || stateUpper === 'TRUE') {
      return <CheckCircleIcon sx={{ color: '#66bb6a', fontSize: 16 }} />;
    } else if (stateUpper === 'OFF' || stateUpper === 'DISABLED' || stateUpper === 'FALSE') {
      return <CancelIcon sx={{ color: '#ef9a9a', fontSize: 16 }} />;
    }
    return <RadioButtonCheckedIcon sx={{ color: colors.textTertiary, fontSize: 16 }} />;
  };

  // const formatTimestamp = (isoString: string): string => {
  //   try {
  //     return new Date(isoString).toLocaleString();
  //   } catch {
  //     return isoString;
  //   }
  // };

  const formatMinutesAgo = (minutes: number): string => {
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const handleDeviceClick = (deviceId: string, entries: DeviceStatusEntry[]) => {
    if (!onStatusEntryClick) return;
    if (selectedDeviceId === deviceId) {
      setSelectedDeviceId(null);
      onStatusEntryClick([]);
    } else {
      setSelectedDeviceId(deviceId);
      onStatusEntryClick(entries);
    }
  };

  // Status chip color. For Student rows, derive the tint from the device_type
  // (phone / watch / vehicle) so it matches the map pin and the rest of COP.
  // RP/SRP keep their role color regardless of device type.
  const getEntityColor = (type: string, deviceType?: string): { bg: string; text: string; border: string } => {
    const t = type?.toLowerCase() || '';
    const dt = deviceType?.toLowerCase() || '';

    // Student split by device type (matches map pins).
    if (t === 'student') {
      if (dt.includes('wardriver') || dt.includes('beluga')) {
        return { bg: 'rgba(251,140,0,0.12)', text: '#ffb74d', border: 'rgba(251,140,0,0.3)' };
      }
      if (dt.includes('vehicle')) return { bg: 'rgba(156,39,176,0.12)', text: '#ba68c8', border: 'rgba(156,39,176,0.3)' };
      if (dt.includes('watch')) return { bg: 'rgba(33,150,243,0.12)', text: '#64b5f6', border: 'rgba(33,150,243,0.3)' };
      // phone or unknown student device -> green
      return { bg: 'rgba(76,175,80,0.12)', text: '#66bb6a', border: 'rgba(76,175,80,0.3)' };
    }

    if (t === 'rp') return { bg: 'rgba(255,152,0,0.12)', text: '#ffb74d', border: 'rgba(255,152,0,0.3)' };
    if (t === 'srp') return { bg: 'rgba(244,67,54,0.12)', text: '#ef9a9a', border: 'rgba(244,67,54,0.3)' };
    if (!t || t === 'unknown') {
      return { bg: 'rgba(117,117,117,0.12)', text: '#9e9e9e', border: 'rgba(117,117,117,0.3)' };
    }
    // Commercial deployments have arbitrary entity types (Operator, Manpack, ...).
    // Pick a deterministic color so each type renders consistently across the UI
    // instead of defaulting to gray.
    const palette: Array<{ bg: string; text: string; border: string }> = [
      { bg: 'rgba(41,182,246,0.12)', text: '#4fc3f7', border: 'rgba(41,182,246,0.3)' },
      { bg: 'rgba(171,71,188,0.12)', text: '#ba68c8', border: 'rgba(171,71,188,0.3)' },
      { bg: 'rgba(38,166,154,0.12)', text: '#4db6ac', border: 'rgba(38,166,154,0.3)' },
      { bg: 'rgba(236,64,122,0.12)', text: '#f06292', border: 'rgba(236,64,122,0.3)' },
      { bg: 'rgba(126,87,194,0.12)', text: '#9575cd', border: 'rgba(126,87,194,0.3)' },
      { bg: 'rgba(255,193,7,0.12)',  text: '#ffd54f', border: 'rgba(255,193,7,0.3)' },
    ];
    let hash = 0;
    for (let i = 0; i < t.length; i++) hash = (hash * 31 + t.charCodeAt(i)) >>> 0;
    return palette[hash % palette.length];
  };

  const deviceGroups = useMemo(() => {
    const grouped: { [key: string]: { displayName: string; entityType: string; entries: DeviceStatusEntry[] } } = {};
    filteredEntries.forEach(entry => {
      const displayName = entry.entity_id || entry.device_id;
      const entityType = entry.entity_type || 'Unknown';
      if (!grouped[displayName]) {
        grouped[displayName] = { displayName, entityType, entries: [] };
      }
      grouped[displayName].entries.push(entry);
    });
    return Object.entries(grouped);
  }, [filteredEntries]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, filteredEntries.length, selectedCourse, selectedDeviceType, selectedEntityType, selectedEntity]);

  const groupCount = deviceGroups.length;
  const safePage = Math.min(page, Math.max(0, Math.ceil(groupCount / rowsPerPage) - 1 || 0));
  const pagedGroups = useMemo(
    () => deviceGroups.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage),
    [deviceGroups, safePage, rowsPerPage],
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header Card */}
      <Paper sx={{ 
        background: colors.cardBg,
        border: `1px solid ${colors.border}`,
        borderRadius: 3,
        overflow: 'hidden',
        backdropFilter: 'blur(10px)',
        boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.08)',
        mb: 2
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 2,
          borderBottom: `1px solid ${colors.border}`,
          background: colors.headerBg,
        }}>
          <Box sx={{
            width: 32,
            height: 32,
            borderRadius: 2,
            background: isDark 
              ? `linear-gradient(135deg, rgba(${primaryRgb}, 0.2), rgba(${primaryRgb}, 0.1))`
              : `linear-gradient(135deg, rgba(${primaryRgb}, 0.15), rgba(${primaryRgb}, 0.08))`,
            border: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <StatusIcon sx={{ color: primaryColor, fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: colors.text, fontWeight: 'bold', fontSize: '0.85rem' }}>
              Device Status
            </Typography>
            <Typography sx={{ color: colors.textTertiary, fontSize: '0.65rem' }}>
              {filteredEntries.length} status entries
            </Typography>
          </Box>
        </Box>
        <Box sx={{ p: 1.5 }}>
          {devicesInBounds ? (
            <Chip
              icon={<LocationIcon sx={{ fontSize: 14 }} />}
              label={`Filtered by map (${devicesInBounds.length} devices)`}
              size="small"
              sx={{
                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                color: '#64b5f6',
                border: '1px solid rgba(33, 150, 243, 0.2)',
                fontSize: '0.7rem',
                mb: 1.5,
              }}
            />
          ) : (
            <Typography sx={{ color: colors.textTertiary, fontSize: '0.7rem', mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationIcon sx={{ fontSize: 14 }} /> Showing all devices
            </Typography>
          )}
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.textSecondary, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: colors.text,
                backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                borderRadius: 2,
                '& fieldset': { borderColor: colors.border },
                '&:hover fieldset': { borderColor: primaryColor },
              }
            }}
          />
        </Box>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <CircularProgress size={32} sx={{ color: primaryColor }} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          sx={{ 
            mb: 2,
            backgroundColor: 'rgba(244, 67, 54, 0.1)',
            color: colors.text,
            border: '1px solid rgba(244, 67, 54, 0.3)',
            borderRadius: 2,
          }}
        >
          {error}
        </Alert>
      )}

      {/* Status Entries List */}
      {!loading && !error && (
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
            borderRadius: 4,
          },
        }}>
          {filteredEntries.length === 0 ? (
            <Paper sx={{ 
              background: colors.cardBg,
              border: `1px solid ${colors.border}`,
              borderRadius: 3,
              p: 4, 
              textAlign: 'center' 
            }}>
              <StatusIcon sx={{ fontSize: 48, color: colors.textTertiary, mb: 1 }} />
              <Typography sx={{ color: colors.textSecondary, fontSize: '0.85rem' }}>
                {searchTerm ? 'No matches found' : 'No status entries'}
              </Typography>
            </Paper>
          ) : (
            pagedGroups.map(([key, deviceGroup]) => {
              const { displayName, entityType, entries } = deviceGroup;
              const deviceId = entries[0]?.device_id || key;
              const isSelected = selectedDeviceId === deviceId;
              // Use the first entry's device_type so the chip tint mirrors
              // the device's map pin (phone/watch/vehicle).
              const deviceType = entries[0]?.device_type;

              return (
                <Paper
                  key={key}
                  onClick={() => handleDeviceClick(deviceId, entries)}
                  sx={{
                    mb: 2,
                    background: colors.cardBg,
                    border: isSelected ? `2px solid ${primaryColor}` : `1px solid ${colors.border}`,
                    borderRadius: 3,
                    overflow: 'hidden',
                    cursor: onStatusEntryClick ? 'pointer' : 'default',
                    transition: 'all 0.2s',
                    '&:hover': onStatusEntryClick ? {
                      transform: 'translateX(2px)',
                      borderColor: primaryColor,
                    } : {}
                  }}
                >
                  {/* Device Header */}
                  <Box sx={{
                    p: 1.5,
                    background: colors.headerBg,
                    borderBottom: `1px solid ${colors.border}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ color: colors.text, fontWeight: 'bold', fontSize: '0.85rem' }}>
                        {displayName}
                      </Typography>
                      <Chip
                        label={entityType}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          bgcolor: getEntityColor(entityType, deviceType).bg,
                          color: getEntityColor(entityType, deviceType).text,
                          border: `1px solid ${getEntityColor(entityType, deviceType).border}`,
                        }}
                      />
                    </Box>
                    <Chip
                      label={`${entries.length} types`}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                        color: colors.textSecondary,
                      }}
                    />
                  </Box>

                  {/* Status Entries Table */}
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: colors.textSecondary, fontWeight: 'bold', borderBottom: `1px solid ${colors.border}`, fontSize: '0.75rem' }}>
                            Type
                          </TableCell>
                          <TableCell sx={{ color: colors.textSecondary, fontWeight: 'bold', borderBottom: `1px solid ${colors.border}`, fontSize: '0.75rem' }}>
                            State
                          </TableCell>
                          <TableCell sx={{ color: colors.textSecondary, fontWeight: 'bold', borderBottom: `1px solid ${colors.border}`, fontSize: '0.75rem' }}>
                            Updated
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {entries.map((entry, idx) => (
                          <TableRow key={idx} sx={{ '&:hover': { bgcolor: colors.subtleBg } }}>
                            <TableCell sx={{ borderBottom: `1px solid ${colors.border}`, py: 1 }}>
                              <Chip
                                label={entry.status_type}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  bgcolor: getStatusTypeColor(entry.status_type).bg,
                                  color: getStatusTypeColor(entry.status_type).text,
                                  border: `1px solid ${getStatusTypeColor(entry.status_type).border}`,
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ borderBottom: `1px solid ${colors.border}`, py: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {getStatusIcon(entry.status_state)}
                                <Typography sx={{ color: colors.text, fontSize: '0.8rem' }}>
                                  {entry.status_state}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ borderBottom: `1px solid ${colors.border}`, py: 1 }}>
                              <Typography sx={{ color: colors.textSecondary, fontSize: '0.75rem' }}>
                                {formatMinutesAgo(entry.minutes_ago)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              );
            })
          )}
        </Box>
      )}

      {!loading && !error && groupCount > 0 && (
        <Box sx={{ borderTop: `1px solid ${colors.border}`, flexShrink: 0, mt: 0.5 }}>
          <TablePagination
            component="div"
            count={groupCount}
            page={safePage}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 15, 25, 50]}
            labelRowsPerPage="Devices:"
            sx={{
              minHeight: 40,
              color: colors.textSecondary,
              '& .MuiTablePagination-toolbar': { minHeight: 40, pl: 1, pr: 1 },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: '0.65rem',
              },
              '& .MuiTablePagination-select': { fontSize: '0.7rem' },
              '& .MuiIconButton-root': { color: colors.textSecondary },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default StatusTab;
